import { AuthAccountRegistrationResponse, register } from 'api/auth'
import { LUXURY_ESCAPES } from 'constants/brands'
import { BUSINESS_TRAVELLER_TENANT } from 'constants/tenants'
import config from 'constants/config'
import { getSydneyTimestamp } from 'lib/datetime/dateUtils'
import { sha256 } from 'lib/utils/createHashUtils'
import { sanitizeErrorMessage } from 'lib/utils/redactPii'
import { logDatadog } from 'services/datadogRum'

export interface BusinessTravellerSignupRegistrationResult {
  creditCustomerAccessToken?: string
  creditCustomerEmail?: string
  creditCustomerId?: string
  lebtAccountRegistration: AuthAccountRegistrationResponse
}

interface RegisterBusinessTravellerSignupAccountsParams {
  businessTravellerAccount?: AuthAccountRegistrationResponse
  accountDetails: App.BusinessTraveller.BusinessSignupFormData
  adminEmail: string
  creditCustomerAccessToken?: string
  creditCustomerEmail?: string
  creditCustomerId?: string
  isDedicatedTenantRegistrationEnabled: boolean
  logDatadogEvent?: typeof logDatadog
  password: string
  regionCode: string
  onPersonalAccount?: (account: AuthAccountRegistrationResponse) => void
  registerUser?: typeof register
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message
  }

  if (error != null && typeof error === 'object' && 'message' in error) {
    return String(error.message)
  }

  return 'Unknown error'
}

export async function registerBusinessTravellerSignupAccounts({
  accountDetails,
  businessTravellerAccount,
  adminEmail,
  creditCustomerAccessToken,
  creditCustomerEmail,
  creditCustomerId,
  isDedicatedTenantRegistrationEnabled,
  logDatadogEvent = logDatadog,
  password,
  regionCode,
  registerUser = register,
  onPersonalAccount,
}: RegisterBusinessTravellerSignupAccountsParams): Promise<BusinessTravellerSignupRegistrationResult> {
  const registerPayload = {
    email: adminEmail,
    password,
    givenName: accountDetails.adminDetails.firstName,
    surname: accountDetails.adminDetails.lastName,
    phone: accountDetails.businessDetails.phone.phone,
    phonePrefix: accountDetails.businessDetails.phone.prefix,
    postcode: accountDetails.adminDetails.postcode,
  }

  if (!isDedicatedTenantRegistrationEnabled) {
    const lebtAccountRegistration = await registerUser(registerPayload, { regionCode })

    return { lebtAccountRegistration }
  }

  if (businessTravellerAccount && (businessTravellerAccount.account.tenant !== BUSINESS_TRAVELLER_TENANT ||
    businessTravellerAccount.account.email?.toLowerCase() !== adminEmail.toLowerCase() || !businessTravellerAccount.accessToken)) {
    throw new Error('Please sign in to the Business Traveller account matching your signup email.')
  }

  const hasLinkedCreditAccount = !!creditCustomerAccessToken || !!creditCustomerEmail || !!creditCustomerId

  if (hasLinkedCreditAccount && (!creditCustomerAccessToken || !creditCustomerEmail || !creditCustomerId)) {
    throw new Error('Linked Luxury Escapes account details are incomplete')
  }

  const personalAccountRegistration = hasLinkedCreditAccount ? undefined : await registerUser(registerPayload, {
    brand: LUXURY_ESCAPES,
    includeCredentials: false,
    regionCode,
  })
  if (personalAccountRegistration) onPersonalAccount?.(personalAccountRegistration)
  const personalAccount = hasLinkedCreditAccount
    ? { memberId: creditCustomerId, email: creditCustomerEmail }
    : personalAccountRegistration?.account
  const personalAccountAccessToken = hasLinkedCreditAccount
    ? creditCustomerAccessToken
    : personalAccountRegistration?.accessToken

  if (!personalAccount?.memberId || !personalAccount.email || !personalAccountAccessToken) {
    throw new Error('Linked Luxury Escapes account is missing')
  }

  try {
    const lebtAccountRegistration = businessTravellerAccount ?? await registerUser(registerPayload, {
      brand: config.BRAND,
      regionCode,
    })

    return {
      creditCustomerAccessToken: personalAccountAccessToken,
      creditCustomerEmail: personalAccount.email,
      creditCustomerId: personalAccount.memberId,
      lebtAccountRegistration,
    }
  } catch(error) {
    if (!hasLinkedCreditAccount) {
      logDatadogEvent('Business signup LEBT account registration failed after LE account creation', {
        creditCustomerId: personalAccount.memberId,
        emailHash: sha256(adminEmail.toLowerCase().trim()),
        errorMessage: sanitizeErrorMessage(getErrorMessage(error)),
        logTimestamp: getSydneyTimestamp(),
        regionCode,
      }, 'error')
    }

    throw error
  }
}
